import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import QtyStepper from '../../../components/orizon-ui/QtyStepper';
import {
  TextField, SelectField, TextareaField, FormSection, InfoBox,
} from '../../../components/orizon-ui/FormField';
import { useToast } from '../../../components/orizon-ui/Toast';
import FormModal, { useFormSalva } from '../../../components/colata/FormModal';
import ArticoloPicker from '../../../components/colata/ArticoloPicker';
import { configScorta, formattaQta, formattaData } from '../../../components/gru/scorta';
import {
  ColataError, mapColataError, colataOpzioni, colataArticolo, colataLista, colataRegistraIntervento, colataModificaIntervento,
  nuovaChiaveIdempotenza,
} from '../../../lib/services/colataService';

const arrotonda3 = (n) => Math.round((Number(n) + Number.EPSILON) * 1000) / 1000;

// data ISO -> valore per <input type="datetime-local"> nel fuso del browser
const isoALocale = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const vuoto = (v) => v === undefined || v === null || String(v).trim() === '';

/** Elementi di un dropdown: attivi + quello già selezionato (anche se nel frattempo disattivato). */
const opzioniAttive = (righe, corrente, etichetta) => righe
  .filter((r) => r.attivo || r.id === corrente)
  .map((r) => ({ value: r.id, label: `${etichetta(r)}${r.attivo ? '' : ' (disattivato)'}` }));

/**
 * Nuovo intervento / modifica di un intervento esistente.
 *
 * Il comportamento sulla giacenza arriva dal tipo scelto:
 *   Sostituzione = scarico, Rientrato = carico, Ordinato = nessuna variazione.
 *
 * Modifica: se cambia tipo, articolo o quantità di un intervento con movimento, il server registra
 * in un'unica transazione lo storno del movimento originale e un nuovo intervento (l'originale resta
 * come "Annullato"); altrimenti aggiorna semplicemente la riga e lo Storico mostra i dati nuovi.
 */
const FormIntervento = ({ intervento = null, onClose, onSaved }) => {
  const modifica = Boolean(intervento);
  const toast = useToast();
  const [chiave] = useState(() => nuovaChiaveIdempotenza());

  const [tipi, setTipi] = useState([]);
  const [elencoZone, setElencoZone] = useState([]);
  const [cause, setCause] = useState([]);
  const [elencoImpianti, setElencoImpianti] = useState([]);
  const [elencoLinee, setElencoLinee] = useState([]);
  const [componenti, setComponenti] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [caricamentoArticolo, setCaricamentoArticolo] = useState(modifica);

  const [tipoId, setTipoId] = useState(intervento?.tipo_id ?? '');
  const [zonaId, setZonaId] = useState(intervento?.zona_id ?? '');
  const [impiantoId, setImpiantoId] = useState(intervento?.impianto_id ?? '');
  const [lineaId, setLineaId] = useState(intervento?.linea_id ?? '');
  const [componenteId, setComponenteId] = useState(intervento?.componente_id ?? '');
  const [articolo, setArticolo] = useState(null);
  const [qta, setQta] = useState(modifica ? Number(intervento.qta) : 1);
  const [causaId, setCausaId] = useState(intervento?.causa_id ?? '');
  const [dataIntervento, setDataIntervento] = useState(() => isoALocale(intervento?.data_intervento));
  const [consegna, setConsegna] = useState(intervento?.data_consegna_prevista ?? '');
  const [nota, setNota] = useState(intervento?.note ?? '');
  // Rientrato: ordine aperto da chiudere (in tutto o in parte) con questo rientro
  const [ordiniAperti, setOrdiniAperti] = useState([]);
  const [ordineId, setOrdineId] = useState('');

  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();

  const articoloId = articolo ? articolo.id : (modifica ? intervento.articolo_id : '');
  const stato = { tipoId, zonaId, impiantoId, lineaId, componenteId, articoloId, qta, causaId, dataIntervento, consegna, nota };
  const baseRef = useRef(JSON.stringify(stato));
  const sporco = modifica
    ? JSON.stringify(stato) !== baseRef.current
    : Boolean(tipoId || zonaId || impiantoId || lineaId || componenteId || articolo || causaId || consegna || nota);

  // un ordine già ricevuto non può cambiare tipo, articolo e quantità
  const bloccaGiacenza = modifica && intervento.stato === 'ricevuto';

  useEffect(() => {
    let annullato = false;
    Promise.all([colataOpzioni('tipi'), colataOpzioni('zone'), colataOpzioni('cause')])
      .then(([t, g, c]) => {
        if (annullato) return;
        setTipi(t);
        setElencoZone(g);
        setCause(c);
        setCaricamento(false);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapColataError(e).message);
        setCaricamento(false);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // modifica: carica l'articolo corrente dell'intervento
  useEffect(() => {
    if (!modifica) return undefined;
    let annullato = false;
    colataArticolo(intervento.articolo_id)
      .then((a) => {
        if (annullato) return;
        setArticolo(a);
        setCaricamentoArticolo(false);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapColataError(e).message);
        setCaricamentoArticolo(false);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // impianti e linee della zona scelta (in modifica, con la zona originale, si conservano quelli già impostati)
  useEffect(() => {
    const conserva = modifica && zonaId === intervento.zona_id;
    if (!conserva) {
      setImpiantoId('');
      setLineaId('');
    }
    if (!zonaId) {
      setElencoImpianti([]);
      setElencoLinee([]);
      return undefined;
    }
    let annullato = false;
    Promise.all([colataOpzioni('impianti', { zona_id: zonaId }), colataOpzioni('linee', { zona_id: zonaId })])
      .then(([imp, lin]) => {
        if (annullato) return;
        setElencoImpianti(imp);
        setElencoLinee(lin);
      })
      .catch((e) => {
        if (!annullato) toast.error(mapColataError(e).message);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zonaId]);

  // componenti dell'impianto scelto (cambiando impianto il componente va riscelto)
  useEffect(() => {
    const conserva = modifica && impiantoId === (intervento.impianto_id ?? '');
    if (!conserva) setComponenteId('');
    if (!impiantoId) {
      setComponenti([]);
      return undefined;
    }
    let annullato = false;
    colataOpzioni('componenti', { impianto_id: impiantoId })
      .then((righe) => {
        if (!annullato) setComponenti(righe.filter((c) => c.attivo || c.id === intervento?.componente_id));
      })
      .catch((e) => {
        if (!annullato) toast.error(mapColataError(e).message);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impiantoId]);

  const tipo = tipi.find((t) => t.id === tipoId);
  const movimento = tipo ? tipo.tipo_movimento : undefined; // 'sostituzione' | 'rientrato' | null (ordine) | undefined (non scelto)
  const eOrdine = tipo && movimento === null;
  const decimali = Number(articolo?.unita_decimali ?? intervento?.unita_decimali ?? 0);

  // Nuovo Rientrato: se l'articolo ha ordini aperti li propone. Con un solo ordine aperto lo preseleziona
  // (si può cambiare o scegliere "Nessun ordine").
  const articoloOrdiniId = articolo?.id ?? null;
  useEffect(() => {
    setOrdineId('');
    setOrdiniAperti([]);
    if (modifica || movimento !== 'rientrato' || !articoloOrdiniId) return undefined;
    let annullato = false;
    colataLista('interventi', {
      perPagina: 50,
      soloAttivi: false,
      sort: { key: 'data_intervento', direction: 'asc' },
      filtri: { articolo_id: articoloOrdiniId, stato: 'ordinato' },
    })
      .then((r) => {
        if (annullato) return;
        const aperti = r.righe.filter((o) => o.tipo_movimento === null);
        setOrdiniAperti(aperti);
        if (aperti.length === 1) setOrdineId(aperti[0].id);
      })
      .catch((e) => {
        if (!annullato) toast.error(mapColataError(e).message);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articoloOrdiniId, movimento, modifica]);

  const ordineScelto = ordiniAperti.find((o) => o.id === ordineId) ?? null;
  const residuoOrdine = ordineScelto ? Number(ordineScelto.qta_residua) : 0;
  const opzioniOrdini = ordiniAperti.map((o) => ({
    value: o.id,
    label: `Ordine del ${formattaData(o.data_ordine || o.data_intervento)} · ordinati ${formattaQta(o.qta, decimali)} · ricevuti ${formattaQta(o.qta_ricevuta, decimali)} · residuo ${formattaQta(o.qta_residua, decimali)} ${o.unita_label}`,
  }));

  // Cosa succede al salvataggio (solo modifica)
  const tipoOriginale = modifica ? intervento.tipo_movimento : undefined; // con movimento: 'sostituzione' | 'rientrato'; ordine: null
  const tocca = modifica && (
    tipoId !== intervento.tipo_id
    || (tipoOriginale !== null && (articoloId !== intervento.articolo_id || Number(qta) !== Number(intervento.qta)))
  );

  // giacenza dopo il salvataggio. In modifica si parte da quella senza l'effetto dell'intervento originale.
  const giacenzaDopo = useMemo(() => {
    if (!articolo || !tipo) return null;
    let base = Number(articolo.qta);
    if (modifica && tocca && tipoOriginale && articolo.id === intervento.articolo_id) {
      base = tipoOriginale === 'sostituzione'
        ? base + Number(intervento.qta)
        : base - Number(intervento.qta);
    }
    if (movimento === 'sostituzione') return arrotonda3(base - qta);
    if (movimento === 'rientrato') return arrotonda3(base + qta);
    return arrotonda3(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articolo, tipo, movimento, qta, tocca]);

  const opzioniTipi = useMemo(() => opzioniAttive(tipi, intervento?.tipo_id, (t) => t.label), [tipi, intervento]);
  const opzioniZone = useMemo(() => opzioniAttive(elencoZone, intervento?.zona_id, (g) => `${g.codice} — ${g.nome}`), [elencoZone, intervento]);
  const opzioniCause = useMemo(() => opzioniAttive(cause, intervento?.causa_id, (c) => c.label), [cause, intervento]);
  const opzioniImpianti = useMemo(() => opzioniAttive(elencoImpianti, intervento?.impianto_id, (i) => i.nome), [elencoImpianti, intervento]);
  const opzioniLinee = useMemo(() => opzioniAttive(elencoLinee, intervento?.linea_id, (l) => l.nome), [elencoLinee, intervento]);
  // l'impianto è obbligatorio se la zona ne ha di attivi; la linea compare solo se la zona ne ha
  const haImpianti = elencoImpianti.some((i) => i.attivo);
  const haLinee = elencoLinee.some((l) => l.attivo) || Boolean(lineaId);
  // un intervento vecchio senza impianto (stessa zona) può restare senza
  const impiantoFacoltativo = modifica && !intervento.impianto_id && zonaId === intervento.zona_id;
  const opzioniComponenti = useMemo(() => componenti.map((c) => ({
    value: c.id,
    label: `${c.reparto_label}${c.posizione ? ` ${c.posizione}` : ''}${c.etichetta ? ` — ${c.etichetta}` : ''}`,
  })), [componenti]);

  const invia = async () => {
    const nuovi = {};
    if (vuoto(tipoId)) nuovi.tipo_id = 'Obbligatorio';
    if (vuoto(zonaId)) nuovi.zona_id = 'Obbligatorio';
    if (haImpianti && vuoto(impiantoId) && !impiantoFacoltativo) nuovi.impianto_id = 'Obbligatorio';
    if (!articolo) nuovi.articolo = 'Seleziona un articolo';
    if (!(Number(qta) > 0)) nuovi.qta = 'Inserisci una quantità maggiore di 0';
    if (vuoto(dataIntervento)) nuovi.data = 'Obbligatorio';
    else if (new Date(dataIntervento).getTime() > Date.now() + 24 * 3600 * 1000) nuovi.data = 'La data non può essere nel futuro';
    if (eOrdine && !vuoto(consegna) && consegna < dataIntervento.slice(0, 10)) {
      nuovi.consegna = 'Precedente alla data dell\'ordine';
    }
    if (movimento === 'sostituzione' && giacenzaDopo !== null && giacenzaDopo < 0) {
      nuovi.qta = `Giacenza insufficiente (disponibili ${formattaQta(giacenzaDopo + Number(qta), decimali)})`;
    }
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const parametri = {
      chiave,
      tipoId,
      zonaId,
      articoloId: articolo.id,
      qta: Number(qta),
      impiantoId: impiantoId || null,
      lineaId: lineaId || null,
      componenteId: componenteId || null,
      causaId: causaId || null,
      dataIntervento: new Date(dataIntervento).toISOString(),
      note: vuoto(nota) ? null : nota.trim(),
      consegnaPrevista: eOrdine && !vuoto(consegna) ? consegna : null,
      ...(!modifica && ordineId ? { ordineId } : {}),
    };

    await salva(
      async () => {
        try {
          return modifica
            ? await colataModificaIntervento({ ...parametri, interventoId: intervento.id })
            : await colataRegistraIntervento(parametri);
        } catch (e) {
          const err = mapColataError(e);
          // errori di quantità e componente: segnalati sul campo giusto
          if (err.code === 'QTA_INSUFFICIENTE' || err.code === 'QTA_NON_VALIDA') {
            throw new ColataError({ code: err.code, message: err.message, campo: 'qta', detail: err.detail, originale: err.originale });
          }
          if (err.code === 'COMPONENTE_NON_VALIDO') {
            throw new ColataError({ code: err.code, message: err.message, campo: 'componente_id', detail: err.detail, originale: err.originale });
          }
          if (err.code === 'IMPIANTO_NON_VALIDO' || err.code === 'IMPIANTO_OBBLIGATORIO') {
            throw new ColataError({ code: err.code, message: err.message, campo: 'impianto_id', detail: err.detail, originale: err.originale });
          }
          if (err.code === 'LINEA_NON_VALIDA') {
            throw new ColataError({ code: err.code, message: err.message, campo: 'linea_id', detail: err.detail, originale: err.originale });
          }
          throw err;
        }
      },
      (r) => {
        if (modifica) {
          if (r.modalita === 'nessuna_modifica') return 'Nessuna modifica da salvare.';
          if (r.modalita === 'storno') return 'Intervento corretto: movimento originale stornato e nuovo movimento registrato.';
          return 'Intervento aggiornato.';
        }
        if (r.stato === 'ordinato') return 'Ordine registrato.';
        const base = `Intervento registrato: giacenza ${formattaQta(r.qta, decimali)} ${articolo.unita_label}.`;
        if (r.ordine_stato === 'ricevuto') return `${base} Ordine ricevuto.`;
        if (r.ordine_stato === 'ordinato') return `${base} L'ordine resta aperto con il residuo.`;
        return base;
      },
      onSaved,
    );
  };

  const cfgDopo = giacenzaDopo === null || !articolo
    ? null
    : configScorta(giacenzaDopo <= 0 ? 'esaurito' : giacenzaDopo < Number(articolo.qta_min ?? 0) ? 'sotto_scorta' : 'ok');

  return (
    <FormModal
      titolo={modifica ? 'Modifica intervento' : 'Nuovo intervento'}
      etichetta="Interventi Colata"
      larghezza="xl"
      sporco={sporco}
      salvando={salvando}
      salvaLabel={modifica ? 'Salva modifiche' : (eOrdine ? 'Registra ordine' : 'Registra intervento')}
      onClose={onClose}
      onSubmit={invia}
    >
      {modifica && caricamentoArticolo && (
        <div className="flex items-center gap-3 text-muted-foreground/50">
          <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          <span className="text-[10px] font-black uppercase tracking-widest">Caricamento intervento...</span>
        </div>
      )}

      {modifica && (
        <InfoBox tono={tocca ? 'warn' : 'info'}>
          {tocca && tipoOriginale !== null && 'Cambiando tipo, articolo o quantità verrà registrato lo storno del movimento originale e un nuovo movimento: nello Storico vedrai tre righe (originale stornato, storno, nuovo). L\'intervento originale risulterà Annullato.'}
          {tocca && tipoOriginale === null && 'Cambiando il tipo, l\'ordine originale verrà annullato e ne verrà creato uno nuovo.'}
          {!tocca && 'Modifica semplice: nessun nuovo movimento, lo Storico mostrerà i dati aggiornati.'}
        </InfoBox>
      )}

      <FormSection titolo="Cosa e dove" icona="Wrench">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <SelectField
            label="Tipo di intervento" name="tipo_id" value={tipoId}
            onChange={(v) => { setTipoId(v); pulisci('tipo_id'); pulisci('qta'); }}
            opzioni={opzioniTipi} obbligatorio errore={errori.tipo_id}
            disabilitato={caricamento || bloccaGiacenza}
          />
          <TextField
            label="Data e ora" name="data" type="datetime-local" value={dataIntervento}
            onChange={(v) => { setDataIntervento(v); pulisci('data'); }}
            obbligatorio errore={errori.data}
          />
          <SelectField
            label="Zona" name="zona_id" value={zonaId}
            onChange={(v) => { setZonaId(v); pulisci('zona_id'); pulisci('impianto_id'); pulisci('componente_id'); }}
            opzioni={opzioniZone} obbligatorio errore={errori.zona_id} disabilitato={caricamento}
          />
          {zonaId && haImpianti && (
            <SelectField
              label="Impianto" name="impianto_id" value={impiantoId}
              onChange={(v) => { setImpiantoId(v); pulisci('impianto_id'); pulisci('componente_id'); }}
              opzioni={opzioniImpianti} obbligatorio={!impiantoFacoltativo} errore={errori.impianto_id}
            />
          )}
          {zonaId && haLinee && (
            <SelectField
              label="Linea" name="linea_id" value={lineaId}
              onChange={(v) => { setLineaId(v); pulisci('linea_id'); }}
              opzioni={opzioniLinee} segnaposto="— Nessuna —" errore={errori.linea_id}
              suggerimento="Facoltativa."
            />
          )}
          <SelectField
            label="Componente" name="componente_id" value={componenteId}
            onChange={(v) => { setComponenteId(v); pulisci('componente_id'); }}
            opzioni={opzioniComponenti}
            segnaposto={!zonaId ? 'Scegli prima la zona' : !impiantoId ? 'Scegli prima l\'impianto' : '— Nessuno —'}
            disabilitato={!impiantoId} errore={errori.componente_id}
            suggerimento="Facoltativo. Se scelto, vedrai gli articoli compatibili."
          />
        </div>

        {tipo && (
          <InfoBox tono={movimento === 'sostituzione' ? 'warn' : 'info'}>
            {movimento === 'sostituzione' && 'Sostituzione: la quantità viene scaricata dalla giacenza.'}
            {movimento === 'rientrato' && 'Rientrato: la quantità viene caricata in giacenza.'}
            {movimento === null && 'Ordine: la giacenza non cambia. Quando la merce arriva, registra la ricezione dal dettaglio dell\'ordine.'}
          </InfoBox>
        )}
      </FormSection>

      <FormSection titolo="Articolo e quantità movimentata" icona="Package">
        <ArticoloPicker
          value={articolo}
          onChange={(a) => { setArticolo(a); if (!modifica) setQta(1); pulisci('articolo'); pulisci('qta'); }}
          componenteId={componenteId || null}
          errore={errori.articolo}
          disabilitato={salvando || bloccaGiacenza}
        />

        {articolo && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Quantità movimentata</span>
              <QtyStepper
                value={qta}
                onChange={(v) => { setQta(v); pulisci('qta'); }}
                originale={modifica ? Number(intervento.qta) : undefined}
                decimali={decimali}
                unitaLabel={articolo.unita_label}
                colore={errori.qta ? 'text-red-500' : 'text-foreground'}
                disabilitato={salvando || bloccaGiacenza}
              />
              {errori.qta && (
                <p role="alert" className="text-[10px] font-black uppercase tracking-widest text-red-500/80 flex items-center gap-1.5">
                  <Icon name="AlertCircle" size={11} />
                  {errori.qta}
                </p>
              )}
            </div>

            {tipo && cfgDopo && (
              <div className="flex flex-wrap items-center gap-2 px-4 py-2 rounded-xl bg-primary/5 border border-primary/15">
                <Icon name="ArrowRightLeft" size={12} className="text-primary/60 shrink-0" />
                <span className="text-[10px] font-black uppercase tracking-widest text-primary/60">
                  Giacenza:&nbsp;{formattaQta(articolo.qta, decimali)}&nbsp;→&nbsp;
                  <span className={cfgDopo.colore}>{formattaQta(giacenzaDopo, decimali)} {articolo.unita_label}</span>
                </span>
              </div>
            )}

            {/* Rientrato: chiusura (anche parziale) di un ordine aperto */}
            {!modifica && movimento === 'rientrato' && ordiniAperti.length > 0 && (
              <div className="flex flex-col gap-3 pt-2 border-t border-border/20">
                <SelectField
                  label="Chiudi un ordine con questo rientro" name="ordine_id" value={ordineId}
                  onChange={setOrdineId}
                  opzioni={opzioniOrdini} segnaposto="— Nessun ordine —"
                  suggerimento="La giacenza aumenta solo una volta, con il rientro: l'ordine viene aggiornato senza altri movimenti."
                />
                {ordineScelto && (
                  <InfoBox tono={Number(qta) >= residuoOrdine ? 'info' : 'warn'}>
                    {Number(qta) >= residuoOrdine
                      ? `Il rientro (${formattaQta(qta, decimali)} ${articolo.unita_label}) copre il residuo dell'ordine (${formattaQta(residuoOrdine, decimali)}): l'ordine passerà a Ricevuto.`
                      : `Ricezione parziale: l'ordine resterà aperto con un residuo di ${formattaQta(residuoOrdine - Number(qta), decimali)} ${articolo.unita_label}.`}
                  </InfoBox>
                )}
              </div>
            )}
          </div>
        )}
      </FormSection>

      <FormSection titolo="Dettagli" icona="FileText">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <SelectField
            label="Causa" name="causa_id" value={causaId}
            onChange={setCausaId} opzioni={opzioniCause} segnaposto="— Nessuna —"
            disabilitato={caricamento} errore={errori.causa_id}
          />
          {eOrdine && (
            <TextField
              label="Consegna prevista" name="consegna" type="date" value={consegna}
              onChange={(v) => { setConsegna(v); pulisci('consegna'); }}
              errore={errori.consegna}
            />
          )}
        </div>
        <TextareaField label="Nota" name="nota" value={nota} onChange={setNota} righe={3} errore={errori.nota} />
      </FormSection>
    </FormModal>
  );
};

export default FormIntervento;
